// Column sets for the three IM-specific Item Master sub-tabs (served by ItemManagementController → camelCase).
// Captions mirror the Delphi grids; visibility matches each screen's saved layout (rest via column chooser).
import { ITEM_STATUSES, TRANSACTION_TYPES, STATUSES } from "../transactionsTab/codeLists";
import { LotStatus } from "../../../const/Lookup";

const money = { type: "fixedPoint", precision: 2 };
const txt = (dataField, caption, visible = false) => ({ dataField, caption, visible });
const num = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "number", format: money });
const dt = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "date", format: "MM/dd/yyyy" });
const dtm = (dataField, caption, visible = false) => ({ dataField, caption, visible, dataType: "datetime", format: "MM/dd/yyyy hh:mm a" });
// User-defined passthroughs: `${labelPrefix} ${n}` (+ optional scope suffix, e.g. "Code 1 - Lot").
const udf = (fieldPrefix, labelPrefix, scope, kind) =>
  Array.from({ length: scope === "Lot" ? 10 : 5 }, (_, i) => {
    const n = i + 1;
    const field = `${fieldPrefix}${n}`;
    const label = scope ? `${labelPrefix} ${n} - ${scope}` : `${labelPrefix} ${n}`;
    return kind === "date" ? dt(field, label) : kind === "num" ? num(field, label) : txt(field, label);
  });

// User-defined passthroughs with an explicit count (e.g. Vendor CR has 10 for both Item and Lot).
const udfN = (fieldPrefix, labelPrefix, scope, count, kind) =>
  Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    const field = `${fieldPrefix}${n}`;
    const label = `${labelPrefix} ${n} - ${scope}`;
    return kind === "date" ? dt(field, label) : kind === "num" ? num(field, label) : txt(field, label);
  });

// "Item Transactions" (Delphi frmIMM0050) — one row per IMTRANSACTIONDETAIL, joined to header + lot.
// Filtered by the selected item (Item Master tab) or the selected lot (Item/Lot tab).
export const ITEM_TRANSACTION_COLUMNS = [
  // Visible (matches the Delphi saved layout). Trans type renders the descriptive word (Adjustment, …).
  { dataField: "transactionType", caption: "Trans Type", visible: true, lookup: { dataSource: TRANSACTION_TYPES, valueExpr: "code", displayExpr: "label" } },
  dt("transactionDate", "Transaction", true),
  txt("reference", "Reference #", true),
  txt("lotCode", "Lot", true),
  txt("lotDescription", "Lot Description - 1", true),
  num("amount", "Amount", true),
  // Hidden — header/detail.
  txt("recordNumber", "Record #"),
  { dataField: "status", caption: "Status", visible: false, lookup: { dataSource: STATUSES, valueExpr: "code", displayExpr: "label" } },
  num("quantity", "Quantity"),
  num("unitCost", "Unit Cost"),
  // Hidden — lot descriptive.
  { dataField: "lotStatus", caption: "Lot Status", visible: false, lookup: { dataSource: LotStatus, valueExpr: "key", displayExpr: "value" } },
  txt("classCode", "Lot Class"),
  txt("subClassCode", "Lot Subclass"),
  dt("dateIntroduced", "Date Introduced"),
  dt("dateAvailable", "Date Available"),
  dt("dateExpiration", "Date Expiration"),
  txt("lotDescription2", "Lot Description - 2"),
  // Hidden — detail user-defined (1..5).
  ...udf("detailCode", "Code", "Detail", "txt"),
  ...udf("detailReference", "Reference", "Detail", "txt"),
  ...udf("detailDate", "Date", "Detail", "date"),
  ...udf("detailQuantity", "Quantity", "Detail", "num"),
  // Hidden — detail audit.
  dtm("creationDateTime", "Creation Date"),
  txt("creationUserId", "Created By"),
  dtm("lastUpdateDateTime", "Last Updated"),
  txt("lastUpdateUserId", "Last Updated By"),
  // Hidden — lot user-defined (1..10).
  ...udf("lotCode", "Code", "Lot", "txt"),
  ...udf("lotReference", "Reference", "Lot", "txt"),
  ...udf("lotDate", "Date", "Lot", "date"),
  ...udf("lotQuantity", "Quantity", "Lot", "num"),
];

// "Vendor Cross Reference" — the item's vendor cross-reference rows (reuses VendorCrDetailDto).
// "Vendor Cross Reference" (Delphi frmIMM0052.qryIMVendorCRDetail) — exact caption/order/visibility parity.
// Default-visible = the fields the Delphi grid shows (Visible=True); the rest are chooser-only. Price Code,
// Purchase User, Inventory User, and the Comment memos are not projected (no EF mapping / niche).
export const VENDOR_CR_COLUMNS = [
  // Visible by default (Delphi Visible = True).
  txt("vendorId", "Vendor", true),
  txt("vendorItemCode", "Vendor Item Code", true),
  num("ranking", "Ranking", true),
  txt("purchaseUom", "Purchase UOM", true),
  txt("inventoryUom", "Inventory UOM", true),
  txt("priceMethod", "Price Method", true),
  num("purchaseCost", "Purchase Cost", true),
  num("salesUnitCost", "Sales Unit Cost", true),
  // Hidden (Delphi chooser columns), in DFM order.
  txt("warehouse", "Warehouse"),
  txt("warehouseName", "Warehose Name"),
  txt("vendorName", "Vendor Name"),
  dt("expiration", "Expiration"),
  txt("reference", "Reference"),
  num("markupPercent", "Markup %"),
  num("grossMarginPercent", "Gr. Margin %"),
  { dataField: "status", caption: "Status", visible: false, lookup: { dataSource: ITEM_STATUSES, valueExpr: "code", displayExpr: "label" } },
  num("msrp", "MSRP"),
  txt("description", "Description"),
  txt("headerReference", "Reference"),
  txt("headerDescription", "Description Header"),
  txt("itemCode", "Item"),
  txt("itemType", "Item Type"),
  txt("itemDescription1", "Item Description - 1"),
  txt("itemDescription2", "Item Description - 2"),
  txt("inventoryUnitUom", "Inventory Unit UOM"),
  txt("lotCode", "Lot"),
  txt("lotDescription1", "Lot Description - 1"),
  txt("lotDescription2", "Lot Description - 2"),
  txt("subLotCode", "SubLot"),
  txt("subLotDescription1", "Sublot Description - 1"),
  txt("subLotDescription2", "Sublot Description - 2"),
  txt("unitCode", "Unit"),
  dtm("creationDateTime", "Creation Date"),
  txt("creationUserId", "Created By"),
  dtm("lastUpdateDateTime", "Last Updated"),
  txt("lastUpdateUserId", "Last Updated By"),
  txt("recordNumber", "Record #"),
  ...udfN("itemCode", "Code", "Item", 10, "txt"),
  ...udfN("itemReference", "Reference", "Item", 10, "txt"),
  ...udfN("itemDate", "Date", "Item", 10, "date"),
  ...udfN("itemQuantity", "Quantity", "Item", 10, "num"),
  ...udfN("lotCode", "Code", "Lot", 10, "txt"),
  ...udfN("lotReference", "Reference", "Lot", 10, "txt"),
  ...udfN("lotDate", "Date", "Lot", 10, "date"),
  ...udfN("lotQuantity", "Quantity", "Lot", 10, "num"),
];

// "Open" / "Historical Transactions" — IMTRANSACTIONHEADER rows (all IM transactions; no Stage column).
export const TRANSACTION_HEADER_COLUMNS = [
  txt("recordNumber", "Record #", true),
  { dataField: "transactionType", caption: "Trans Type", visible: true, lookup: { dataSource: TRANSACTION_TYPES, valueExpr: "code", displayExpr: "label" } },
  dt("transactionDate", "Trans Date", true),
  txt("reference", "Reference #", true),
  { dataField: "status", caption: "Status", visible: true, lookup: { dataSource: STATUSES, valueExpr: "code", displayExpr: "label" } },
  txt("description", "Description", true),
  num("totalAmount", "Total Amount", true),
  // Hidden.
  num("totalQuantity", "Total Quantity"),
  num("fiscalYear", "Fiscal Year"),
  num("fiscalPeriod", "Fiscal Period"),
];
